import { gql, NetworkStatus } from '@apollo/client';
import { OrderHistoryListOption } from '@/utils/order-helpers';
import {
  Orders_CreditRequestBatchesQuery,
  Orders_CreditRequestBatchesQueryVariables,
} from '@/types';
import { useMemo, useState } from 'react';
import { useToasts } from '@/contexts/toast-context';
import { useUserContext } from '@/contexts/user-context';
import { useWrappedQuery } from './use-wrapped-query';

export const CREDIT_REQUEST_BATCHES_QUERY = gql`
  fragment Orders_CreditRequestBatchLineItemFields on credit_request_batch_line_items {
    pack_price_in_micros
    quantity
    uuid
  }

  fragment Orders_CreditRequestBatchFields on credit_request_batches {
    created_at
    customer_facing_id
    credit_request_batch_line_items {
      ...Orders_CreditRequestBatchLineItemFields
    }
    uuid
  }

  query Orders_CreditRequestBatches(
    $chatUUID: uuid!
    $limit: Int!
    $offset: Int
  ) {
    credit_request_batches(
      where: { chat_uuid: { _eq: $chatUUID } }
      order_by: { created_at: desc }
      limit: $limit
      offset: $offset
    ) {
      ...Orders_CreditRequestBatchFields
    }

    credit_request_batches_aggregate(where: { chat_uuid: { _eq: $chatUUID } }) {
      aggregate {
        count
      }
    }
  }
`;

interface CreditRequestBatchesQueryResult {
  creditRequests: OrderHistoryListOption[];
  fetchingMore: boolean;
  fetchMore: () => Promise<void>;
  hasResponse: boolean;
  loading: boolean;
  refresh: (manual?: boolean) => Promise<void>;
  refreshing: boolean;
}

export const CREDIT_REQUEST_BATCHES_PAGE_LIMIT = 20;

export const useGetCreditRequestBatches =
  (): CreditRequestBatchesQueryResult => {
    const [isManualRefresh, setIsManualRefresh] = useState(false);
    const {
      selectedRestaurantRelationship: { chatUUIDs },
    } = useUserContext();
    const chatUUID = chatUUIDs[0];
    const { showErrorToast } = useToasts();

    const { data, fetchMore, loading, hasResponse, networkStatus, refetch } =
      useWrappedQuery<
        Orders_CreditRequestBatchesQuery,
        Orders_CreditRequestBatchesQueryVariables
      >(CREDIT_REQUEST_BATCHES_QUERY, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
        variables: {
          chatUUID,
          limit: CREDIT_REQUEST_BATCHES_PAGE_LIMIT,
          offset: 0,
        },
      });

    const fetchMoreOffset = useMemo(() => {
      const currentCount = data?.credit_request_batches.length ?? 0;
      const totalCount =
        data?.credit_request_batches_aggregate?.aggregate?.count ?? 0;
      return currentCount < totalCount ? currentCount : null;
    }, [data]);

    return useMemo(() => {
      const creditRequestSections = (data?.credit_request_batches || []).reduce<
        OrderHistoryListOption[]
      >((acc, batch) => {
        acc.push({
          metadata: {
            creditRequest: batch,
          },
          title: batch.uuid,
          value: batch.uuid,
        });
        return acc;
      }, []);
      return {
        creditRequests: creditRequestSections,
        fetchingMore: networkStatus === NetworkStatus.fetchMore,
        fetchMore: async () => {
          if (typeof fetchMoreOffset === 'number') {
            await fetchMore<
              Orders_CreditRequestBatchesQuery,
              Orders_CreditRequestBatchesQueryVariables
            >({
              updateQuery: (prevResult, { fetchMoreResult }) => {
                return fetchMoreResult
                  ? {
                      ...prevResult,
                      credit_request_batches: [
                        ...prevResult.credit_request_batches,
                        ...fetchMoreResult.credit_request_batches,
                      ],
                    }
                  : prevResult;
              },
              variables: {
                chatUUID,
                limit: CREDIT_REQUEST_BATCHES_PAGE_LIMIT,
                offset: fetchMoreOffset,
              },
            });
          }
        },
        hasResponse,
        loading,
        refresh: async (manual = false) => {
          try {
            setIsManualRefresh(manual);
            await refetch();
          } catch {
            if (manual) {
              showErrorToast();
            }
          } finally {
            setIsManualRefresh(false);
          }
        },
        refreshing: isManualRefresh && networkStatus === NetworkStatus.refetch,
      };
    }, [
      chatUUID,
      data?.credit_request_batches,
      fetchMore,
      fetchMoreOffset,
      hasResponse,
      isManualRefresh,
      loading,
      networkStatus,
      refetch,
      showErrorToast,
    ]);
  };
